import type { TagType, TagStyle } from "@/components/Tag/Tag";
import type { TagExtras } from "@/components/Table/buildTagColumn";
import type { MaterialNames } from "@/components/material_symbol_names";
import { toDateTimeFormat } from "@/lib/dateHelpers";
import type { BackgroundCheckOrder, BackgroundCheckOrderUpdate } from "./types";

type ScreeningOrderStatusConfig = {
  label: string;
  tagType: TagType;
  tagStyle: TagStyle;
  iconName: MaterialNames;
  tooltipTemplate: (firstName: string) => string;
};

const SCREENING_ORDER_STATUS_CONFIG: Record<string, ScreeningOrderStatusConfig> = {
  Sent: {
    label: "Sent to Applicant",
    tagType: "information",
    tagStyle: "standard",
    iconName: "send",
    tooltipTemplate: (firstName) =>
      `An invitation URL has been created for ${firstName} to access the screening portal.`
  },
  Pending: {
    label: "Pending",
    tagType: "information",
    tagStyle: "standard",
    iconName: "hourglass_empty",
    tooltipTemplate: (firstName) => `The screening order for ${firstName} is waiting on the next step.`
  },
  "Pending Applicant": {
    label: "Pending Applicant",
    tagType: "tertiary",
    tagStyle: "standard",
    iconName: "hourglass_empty",
    tooltipTemplate: (firstName) => `Waiting for ${firstName} to fill out and submit their background forms.`
  },
  "Pending Requestor": {
    label: "Pending Requestor",
    tagType: "warn",
    tagStyle: "standard",
    iconName: "support_agent",
    tooltipTemplate: (firstName) => `Action is required from Membership Team to proceed with ${firstName}'s order.`
  },
  "In-Progress": {
    label: "In Progress",
    tagType: "information",
    tagStyle: "strong",
    iconName: "progress_activity",
    tooltipTemplate: (firstName) => `Verifications and background searches are underway for ${firstName}.`
  },
  Completed: {
    label: "Completed",
    tagType: "success",
    tagStyle: "strong",
    iconName: "info",
    tooltipTemplate: (firstName) =>
      `All screening services for ${firstName} have finished; a final report has been generated.`
  },
  "Invite Expired": {
    label: "Invite Expired",
    tagType: "warn",
    tagStyle: "standard",
    iconName: "link_off",
    tooltipTemplate: (firstName) => `${firstName}'s portal invitation link expired before completion.`
  },
  Canceled: {
    label: "Canceled",
    tagType: "error",
    tagStyle: "standard",
    iconName: "cancel",
    tooltipTemplate: (firstName) => `The screening order for ${firstName} was stopped or canceled.`
  },
  Cancelled: {
    label: "Canceled",
    tagType: "error",
    tagStyle: "standard",
    iconName: "cancel",
    tooltipTemplate: (firstName) => `The screening order for ${firstName} was stopped or canceled.`
  }
};

function screeningOrderStatusFirstName(firstName?: string | null): string {
  const trimmed = firstName?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : "the applicant";
}

export function getOrderStatusDisplayLabel(status: string | null | undefined): string {
  if (!status) {
    return "Not Started";
  }

  return SCREENING_ORDER_STATUS_CONFIG[status]?.label ?? status;
}

export function getOrderStatusTagExtras(
  status: string | null | undefined,
  firstName?: string | null
): TagExtras | undefined {
  const config = status ? SCREENING_ORDER_STATUS_CONFIG[status] : undefined;
  if (!config) {
    return undefined;
  }

  return {
    iconName: config.iconName,
    tooltipText: config.tooltipTemplate(screeningOrderStatusFirstName(firstName))
  };
}

export function formatOrderDateTime(value: string | null | undefined): string {
  if (!value) return "--";
  return toDateTimeFormat(value) ?? "--";
}

export function getOrderStatusTagType(status: string | null | undefined): TagType {
  if (!status) {
    return "surface";
  }

  return SCREENING_ORDER_STATUS_CONFIG[status]?.tagType ?? "surface";
}

export function getOrderStatusTagStyle(status: string | null | undefined): TagStyle {
  if (!status) {
    return "standard";
  }

  return SCREENING_ORDER_STATUS_CONFIG[status]?.tagStyle ?? "standard";
}

export function isPendingOrderResult(result: string | null | undefined): boolean {
  const normalized = result?.trim().toLowerCase();
  return !normalized || normalized === "pending";
}

export function getScreeningResultLabel(updates: BackgroundCheckOrderUpdate[]): string {
  return getLatestMeaningfulOrderResult(updates) ?? "Pending";
}

export function getOrderLastActivity(order: BackgroundCheckOrder): string | null {
  const latestUpdate = sortOrderUpdatesNewestFirst(order.updates ?? [])[0];
  return latestUpdate?.createdAt ?? order.updatedAt ?? order.createdAt ?? null;
}

export function getLatestMeaningfulOrderResult(updates: BackgroundCheckOrderUpdate[]): string | null {
  for (const update of sortOrderUpdatesNewestFirst(updates)) {
    if (!isPendingOrderResult(update.result)) {
      return update.result;
    }
  }

  return null;
}

export function sortOrderUpdatesNewestFirst(updates: BackgroundCheckOrderUpdate[]): BackgroundCheckOrderUpdate[] {
  return [...updates].sort((first, second) => Date.parse(second.createdAt) - Date.parse(first.createdAt));
}

export function formatOrderProgress(percentComplete: number | null | undefined): string {
  return percentComplete == null ? "--" : `${percentComplete}%`;
}
